/**
 * Kernel-owned `/dye` wire events (lifecycle + privacy). Each optional module
 * owns its feature events in its own `events.ts` (see `modules/ecom/events.ts`,
 * `modules/pool/events.ts`).
 *
 * Wire values are the yoto names — analytics-consumer maps `type=` strings to
 * ReportedEventType by exact match, so they must not drift.
 */

export const CORE_EVENTS = {
  /** SDK bootstrapped (before consent resolution). */
  sdkInit: 'sdk_init',
  /** First-ever user id issued. */
  userInit: 'user_init',
  /** Returning persistent user id. */
  userExisting: 'user_existing',
  /** New session id issued. */
  sessionInit: 'session_init',
  /** Existing session refreshed. */
  sessionRefresh: 'session_refresh',
  /** Page view (consumer VIEWED_ITEMS). Fired alongside the compact alias. */
  pageView: 'analytics:pageview',
  /** Compact page-view alias (consumer PAGEVIEW). */
  pageViewCompact: 'pv',
  /** Active time-on-site delta in seconds (consumer ITEM_DURATION_SECONDS → time_on_page_sec). */
  itemDuration: 'analytics:item_duration_sec',
  /** CCPA sale opt-out. */
  ccpaOptOut: 'ccpa_opt_out',
  /** Site-logo click (consumer LOGO_CLICK_EVENT). Yoto parity: `logo_click`. */
  logoClick: 'logo_click',
  /**
   * Bot classification (consumer DETECTED_BOTS). `info=` carries the yoto
   * BotDetectionType label. Fired on a path that bypasses the bot short-circuit
   * — otherwise the event that reports bots would drop itself.
   */
  detectedBot: 'isbt',
} as const;

export type CoreEventType = (typeof CORE_EVENTS)[keyof typeof CORE_EVENTS];
